import { useEffect, useRef } from "react";

import type { QueueJobStatus, QueueJobType } from "@/lib/api";
import type { QueueFilters as QueueFiltersState } from "@/state/queueStore";

const JOB_STATUSES: { value: QueueJobStatus; label: string; cls: string }[] = [
  { value: "queued", label: "Na fila", cls: "border-amber-800 bg-amber-900/40 text-amber-300" },
  { value: "dispatched", label: "Despachado", cls: "border-sky-800 bg-sky-900/40 text-sky-300" },
  { value: "preempting", label: "Pausando", cls: "border-fuchsia-800 bg-fuchsia-900/40 text-fuchsia-300" },
  { value: "preempted", label: "Retomado", cls: "border-violet-800 bg-violet-900/40 text-violet-300" },
  { value: "started", label: "Iniciado", cls: "border-blue-800 bg-blue-900/40 text-blue-300" },
  { value: "progress", label: "Em progresso", cls: "border-blue-700 bg-blue-900/50 text-blue-200" },
  { value: "retry", label: "Retry", cls: "border-orange-800 bg-orange-900/40 text-orange-300" },
  { value: "success", label: "Sucesso", cls: "border-emerald-800 bg-emerald-900/40 text-emerald-300" },
  { value: "failure", label: "Falha", cls: "border-red-800 bg-red-900/40 text-red-300" },
  { value: "canceled", label: "Cancelado", cls: "border-ink-600 bg-ink-800 text-ink-400" },
];

const JOB_TYPES: { value: QueueJobType; label: string }[] = [
  { value: "viewer.render", label: "Render v1" },
  { value: "viewer.preprepare", label: "Preprepare" },
  { value: "viewer.rehydrate", label: "Recuperação" },
  { value: "viewer.layers", label: "Camadas" },
  { value: "render.preview", label: "Render" },
  { value: "barcode", label: "Barcode" },
];

const JOB_PRIORITIES: { value: number; label: string }[] = [
  { value: 0, label: "Background" },
  { value: 30, label: "Normal" },
  { value: 70, label: "Alta" },
  { value: 100, label: "Urgente" },
];

interface Props {
  filters: QueueFiltersState;
  onFiltersChange: (partial: Partial<QueueFiltersState>) => void;
  onReset: () => void;
}

export function QueueFilters({ filters, onFiltersChange, onReset }: Props) {
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function toggleStatus(s: QueueJobStatus) {
    const next = filters.statuses.includes(s)
      ? filters.statuses.filter((x) => x !== s)
      : [...filters.statuses, s];
    onFiltersChange({ statuses: next });
  }

  function handleSearchChange(e: React.ChangeEvent<HTMLInputElement>) {
    const val = e.target.value;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onFiltersChange({ q: val });
    }, 300);
  }

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const hasFilters =
    filters.statuses.length > 0 ||
    filters.jobType !== null ||
    filters.priority !== null ||
    filters.q !== "" ||
    filters.from !== "" ||
    filters.to !== "";

  return (
    <div className="flex flex-col gap-3">
      {/* Status chips */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-ink-400">Status:</span>
        {JOB_STATUSES.map(({ value, label, cls }) => {
          const active = filters.statuses.includes(value);
          return (
            <button
              key={value}
              type="button"
              onClick={() => toggleStatus(value)}
              className={`rounded border px-2.5 py-0.5 text-xs font-medium transition-opacity ${
                active ? cls : "border-ink-700 bg-ink-800 text-ink-500 opacity-60 hover:opacity-100"
              }`}
              aria-pressed={active}
            >
              {label}
            </button>
          );
        })}
      </div>

      {/* Type + search row */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-ink-400">Tipo:</span>
        <select
          value={filters.jobType ?? ""}
          onChange={(e) =>
            onFiltersChange({ jobType: (e.target.value as QueueJobType) || null })
          }
          className="rounded border border-ink-700 bg-ink-800 px-2 py-1 text-xs text-ink-200 focus:outline-none focus:ring-1 focus:ring-ink-500"
          aria-label="Filtrar por tipo de job"
        >
          <option value="">Todos</option>
          {JOB_TYPES.map(({ value, label }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>

        <span className="text-xs font-medium text-ink-400">Prioridade:</span>
        <select
          value={filters.priority ?? ""}
          onChange={(e) =>
            onFiltersChange({
              priority: e.target.value === "" ? null : Number.parseInt(e.target.value, 10),
            })
          }
          className="rounded border border-ink-700 bg-ink-800 px-2 py-1 text-xs text-ink-200 focus:outline-none focus:ring-1 focus:ring-ink-500"
          aria-label="Filtrar por prioridade"
        >
          <option value="">Todas</option>
          {JOB_PRIORITIES.map(({ value, label }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>

        <span className="text-xs font-medium text-ink-400">Busca:</span>
        <input
          type="search"
          defaultValue={filters.q}
          onChange={handleSearchChange}
          placeholder="Nome do arquivo, ID, task ID ou mensagem…"
          className="min-w-[14rem] rounded border border-ink-700 bg-ink-800 px-2.5 py-1 text-xs text-ink-200 placeholder-ink-600 focus:outline-none focus:ring-1 focus:ring-ink-500"
          aria-label="Buscar jobs"
        />

        {hasFilters && (
          <button
            type="button"
            onClick={onReset}
            className="ml-auto rounded border border-ink-700 bg-ink-800 px-3 py-1 text-xs text-ink-400 hover:border-ink-500 hover:text-ink-200"
          >
            Limpar filtros
          </button>
        )}
      </div>
    </div>
  );
}
